import React, { useState } from 'react';
import { AnimationOnScroll } from 'react-animation-on-scroll';
import PSMServicesData from '../../data/psm/PSMServicesData.json';
import SectionTitle from '../../elements/section-title/SectionTitle';

const PSMServicesShowcase = () => {
    const [activeService, setActiveService] = useState(PSMServicesData[0]);

    const handleServiceClick = (service) => {
        setActiveService(service);
    };

    return (
        <div className="psm-services-showcase-section">
            <div className="container">
                <SectionTitle
                    subtitle="What We Can Do For You"
                    title="Services we can help you with"
                    textAlignment="heading-center mb--60 heading-light"
                    textColor=""
                />

                {/* Desktop/Tablet View - Interactive */}
                <div className="psm-services-content psm-desktop-view">
                    {/* Left - Service Buttons */}
                    <div className="psm-services-buttons">
                        {PSMServicesData.map((service, index) => (
                            <button
                                key={service.id}
                                className={`psm-service-btn ${activeService.id === service.id ? 'active' : ''}`}
                                onClick={() => handleServiceClick(service)}
                            >
                                <span className="psm-btn-text">{service.name}</span>
                                {activeService.id === service.id && (
                                    <svg className="psm-arrow-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Center - Main Image */}
                    <div className="psm-main-image-wrapper">
                        <AnimationOnScroll animateIn="fadeIn" animateOnce={false} key={activeService.id}>
                            <img
                                src={process.env.PUBLIC_URL + activeService.mainImage}
                                alt={activeService.name}
                                className="psm-main-image"
                            />
                        </AnimationOnScroll>
                    </div>

                    {/* Right - Details Panel */}
                    <div className="psm-details-panel">
                        <AnimationOnScroll animateIn="fadeInRight" animateOnce={false} key={activeService.id}>
                            <div className="psm-details-content">
                                {/* Service Icon */}
                                <div className="psm-service-icon-wrapper">
                                    <img
                                        src={process.env.PUBLIC_URL + activeService.sideImage}
                                        alt={activeService.name}
                                        className="psm-service-icon"
                                    />
                                </div>

                                {/* Service Name */}
                                <h3 className="psm-service-name">{activeService.name}</h3>

                                {/* Features List */}
                                <ul className="psm-features-list">
                                    {activeService.features.map((feature, idx) => (
                                        <li key={idx} className="psm-feature-item">
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </AnimationOnScroll>
                    </div>
                </div>

                {/* Mobile View - All Services as Cards */}
                <div className="psm-mobile-view">
                    {PSMServicesData.map((service, index) => (
                        <div key={service.id} className="psm-mobile-service-card">
                            <AnimationOnScroll animateIn="fadeInUp" animateOnce={true} delay={index * 100}>
                                <div className="psm-mobile-card-content">
                                    {/* Service Icon */}
                                    <div className="psm-mobile-icon-wrapper">
                                        <img
                                            src={process.env.PUBLIC_URL + service.sideImage}
                                            alt={service.name}
                                            className="psm-mobile-icon"
                                        />
                                    </div>

                                    {/* Service Name */}
                                    <h3 className="psm-mobile-service-name">{service.name}</h3>

                                    {/* Features List */}
                                    <ul className="psm-mobile-features-list">
                                        {service.features.map((feature, idx) => (
                                            <li key={idx} className="psm-mobile-feature-item">
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </AnimationOnScroll>
                        </div>
                    ))}
                </div>
            </div>

            <style jsx>{`
                .psm-services-showcase-section {
                    background-color: #2B2B2B;
                    padding: 80px 0;
                    position: relative;
                    overflow: hidden;
                }

                /* Desktop View - Show by default */
                .psm-desktop-view {
                    display: grid;
                }

                /* Mobile View - Hidden by default */
                .psm-mobile-view {
                    display: none;
                }

                .psm-services-content {
                    grid-template-columns: 280px 1fr 350px;
                    gap: 40px;
                    align-items: start; /* Vertically center aligned as usually preferred, can switch to start if needed */
                    margin-top: 60px;
                }

                /* Service Buttons - Left Panel */
                .psm-services-buttons {
                    display: flex;
                    flex-direction: column;
                    gap: 20px; /* Added gap between buttons */
                }

                .psm-service-btn {
                    background-color: transparent;
                    border: none;
                    color: #ffffff;
                    text-align: left;
                    padding: 12px 20px; /* Reduced vertical padding */
                    font-size: 14px; /* Reduced font size to prevent wrapping issues */
                    font-weight: 400;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    border-radius: 50px;
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    line-height: 1.4;
                }

                .psm-btn-text {
                    flex: 1;
                    /* Ensure text wraps nicely if it has to */
                    white-space: normal; 
                }

                .psm-service-btn:hover {
                    background-color: rgba(255, 255, 255, 0.1);
                }

                .psm-service-btn.active {
                    background-color: #ffffff;
                    color: #2B2B2B;
                    font-weight: 500;
                }

                .psm-arrow-icon {
                    margin-left: 10px;
                    flex-shrink: 0;
                }

                /* Main Image - Center */
                .psm-main-image-wrapper {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;  /* Removed padding */
                    background-color: transparent; /* Removed background */
                    border-radius: 0; /* Removed border radius */
                    min-height: auto; /* Removed fixed min-height to fit content */
                }

                .psm-main-image {
                    width: 100%;
                    max-width: 450px;
                    height: auto;
                    object-fit: contain;
                    border-radius: 15px; /* Kept border radius on the image itself */
                }

                /* Details Panel - Right */
                .psm-details-panel {
                    background-color: transparent;
                    padding: 0;
                    text-align: left;
                }

                .psm-details-content {
                    color: #ffffff;
                }

                .psm-service-icon-wrapper {
                    width: 110px;
                    height: 110px;
                    margin-bottom: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-left: 0;
                    margin-right: 0;
                }

                .psm-service-icon {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                }

                .psm-service-name {
                    font-size: 24px;
                    font-weight: 600;
                    color: #ffffff;
                    margin-bottom: 10px;
                    line-height: 1.3;
                }

                .psm-features-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                }

                .psm-feature-item {
                    color: #e0e0e0;
                    font-size: 15px;
                    line-height: 1.6;
                    margin-bottom: 2px;
                    padding-left: 20px;
                    position: relative;
                }

                .psm-feature-item::before {
                    content: "•";
                    color: #ffffff;
                    font-size: 20px;
                    position: absolute;
                    left: 0;
                    top: -2px;
                }

                /* Mobile Card Layout Styles */
                .psm-mobile-service-card {
                    background-color: rgba(255, 255, 255, 0.05);
                    border-radius: 15px;
                    padding: 30px 20px;
                    margin-bottom: 20px;
                }

                .psm-mobile-service-card:last-child {
                    margin-bottom: 0;
                }

                .psm-mobile-card-content {
                    text-align: start;
                }

                .psm-mobile-icon-wrapper {
                    width: 70px;
                    height: 70px;
                    margin: 0 0 20px;
                    background-color: rgba(255, 255, 255, 0.1);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 12px;
                }

                .psm-mobile-icon {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                }

                .psm-mobile-service-name {
                    font-size: 20px;
                    font-weight: 600;
                    color: #ffffff;
                    margin-bottom: 20px;
                    line-height: 1.3;
                }

                .psm-mobile-features-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                    text-align: left;
                }

                .psm-mobile-feature-item {
                    color: #e0e0e0;
                    font-size: 14px;
                    line-height: 1.6;
                    margin-bottom: 8px;
                    padding-left: 20px;
                    position: relative;
                }

                .psm-mobile-feature-item::before {
                    content: "•";
                    color: #ffffff;
                    font-size: 18px;
                    position: absolute;
                    left: 0;
                    top: -2px;
                }

                /* Responsive Styles */
                @media (max-width: 1500px) {
                     .psm-services-content {
                        grid-template-columns: 260px 1fr 320px; /* Slightly reduce side columns */
                    }
                    .psm-service-btn {
                        font-size: 14px; 
                    }
                }

                @media (max-width: 1200px) {
                    .psm-services-content {
                        grid-template-columns: 240px 1fr 300px;
                        gap: 20px;
                    }

                    .psm-service-btn {
                        font-size: 13px; /* Smaller font for tighter spaces */
                        padding: 10px 15px;
                    }

                    .psm-main-image {
                        max-width: 100%; /* Ensure image scales down */
                    }
                }

                @media (max-width: 991px) {
                    .psm-services-content {
                        grid-template-columns: 1fr;
                        gap: 40px;
                    }

                    .psm-services-buttons {
                        flex-direction: row;
                        flex-wrap: wrap;
                        gap: 10px;
                        justify-content: center;
                    }

                    .psm-service-btn {
                        flex: 0 1 auto;
                        font-size: 14px;
                        padding: 12px 18px;
                        white-space: normal;
                        min-width: unset; /* Allow flexible width */
                        max-width: 100%;
                    }

                    .psm-btn-text {
                        white-space: normal;
                        line-height: 1.3;
                    }

                    .psm-main-image-wrapper {
                        order: 2;
                    }

                    .psm-details-panel {
                        order: 3;
                        text-align: center;
                    }

                    .psm-service-icon-wrapper {
                        margin-left: auto;
                        margin-right: auto;
                    }

                    .psm-features-list {
                        text-align: left;
                        max-width: 500px;
                        margin: 0 auto;
                    }
                }

                @media (max-width: 767px) {
                    /* Hide desktop view */
                    .psm-desktop-view {
                        display: none !important;
                    }

                    /* Show mobile view */
                    .psm-mobile-view {
                        display: block;
                        margin-top: 40px;
                    }

                    .psm-services-showcase-section {
                        padding: 60px 0;
                    }
                }

                @media (max-width: 575px) {
                    .psm-mobile-service-card {
                        padding: 25px 15px;
                    }

                    .psm-mobile-service-name {
                        font-size: 18px;
                    }

                    .psm-mobile-feature-item {
                        font-size: 13px;
                    }
                }
            `}</style>
        </div>
    );
};

export default PSMServicesShowcase;

